import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService implements OnDestroy {
  // Session duration constants - now configurable per environment
  private readonly SESSION_DURATION = (environment.sessionTimeout?.sessionDurationMinutes || 120) * 60 * 1000; // Default 2 hours
  private readonly WARNING_BEFORE_TIMEOUT = (environment.sessionTimeout?.warningBeforeTimeoutMinutes || 2) * 60 * 1000; // Default 2 minutes
  private readonly CHECK_INTERVAL = (environment.sessionTimeout?.checkIntervalSeconds || 30) * 1000; // Default 30 seconds
  
  private destroy$ = new Subject<void>();
  private timerSubscription?: Subscription;
  
  private showWarningDialogSubject = new BehaviorSubject<boolean>(false);
  public showWarningDialog$ = this.showWarningDialogSubject.asObservable();

  constructor(private authService: AuthService) {
    this.startExpirationTimer();
    
    // Restart timer when user logs in
    this.authService.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.startExpirationTimer();
      } else {
        this.stopTimer();
      }
    });
  }

  private startExpirationTimer(): void {
    this.stopTimer();
    
    this.timerSubscription = interval(this.CHECK_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const expirationDate = this.authService.getTokenExpirationDate();
        
        if (!expirationDate) {
          this.stopTimer();
          return;
        }

        const timeUntilExpiration = expirationDate.getTime() - Date.now();
        
        // Show warning when less than 5 minutes remaining
        if (timeUntilExpiration <= this.WARNING_BEFORE_TIMEOUT) {
          this.showWarningDialogSubject.next(true);
        }
      });
  }

  private stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.showWarningDialogSubject.next(false);
  }

  public hideWarningDialog(): void {
    this.showWarningDialogSubject.next(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopTimer();
  }
} 